/**
 * Global "session expired" signal.
 *
 * API-layer modules (plain fetch wrappers, not React components) call
 * dispatchSessionExpired() when a 401 cannot be recovered by a token refresh.
 * A single top-level component (SessionExpiredModal) listens and shows a
 * friendly "please sign in again" prompt — so a raw 401 is never surfaced.
 *
 * Uses the same custom-window-event pattern already used elsewhere in the app
 * (e.g. agentRoutingApi / slaSettingsApi).
 */

export const SESSION_EXPIRED_EVENT = "session-expired";

export function dispatchSessionExpired() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/**
 * Subscribe to session-expired events. Returns an unsubscribe function.
 * @param {() => void} handler
 */
export function onSessionExpired(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
}

/**
 * Error thrown by fetch wrappers when a request fails due to an unrecoverable
 * expired session. It is "handled": the global modal owns the UX, so callers
 * and error toasts must NOT surface it as a raw error.
 */
export class SessionExpiredError extends Error {
  constructor(message = "Your session has expired. Please sign in again.") {
    super(message);
    this.name = "SessionExpiredError";
    this.status = 401;
    this.handledBySessionExpiredModal = true;
  }
}

/**
 * Refresh could not complete for a TRANSIENT reason (429 "refresh already in
 * progress", network error, or 5xx). The session is NOT dead — do not clear it
 * and do not show the modal; the request just fails and a later attempt recovers.
 */
export class RefreshTransientError extends Error {
  constructor(message = "Token refresh is temporarily unavailable.") {
    super(message);
    this.name = "RefreshTransientError";
    this.transient = true;
  }
}

/**
 * Refresh was DEFINITIVELY rejected (the refresh token itself is invalid/expired
 * — 401 / invalid_grant). The session is over: clear it and show the modal.
 */
export class RefreshInvalidError extends Error {
  constructor(message = "Session refresh was rejected. Please sign in again.") {
    super(message);
    this.name = "RefreshInvalidError";
  }
}
